import React, {
	useEffect, useMemo, useRef, useState, useLayoutEffect,
} from 'react';

import Portal from '../portal';
import { TTPosition, TTPositionType } from './tooltip-wrapper';

type TooltipStyleBase = {
	animation: string & {};
	left: string | number;
	bottom: string | number;
};
type TooltipStyle = TooltipStyleBase & Partial<React.CSSProperties>;

interface TooltipPosition {
	animation: string;
	left: number;
	bottom?: number;
	top?: number;
}

interface ConditionalPortalProps {
	children: JSX.Element;
	targetId?: string;
	className?: string;
}

interface TooltipProps {
	message: string;
	showing: boolean;
	targetRef: React.MutableRefObject<Element | null>;
	portalId?: string;
	portalClassNameString?: string;
	cssStyles?: TooltipStyle;
	restrictedUser?: boolean;
	mousePos?: {
		x: number;
		y: number;
	};
	colorStyle?: string;
	position?: TTPositionType;
}

const ConditionalPortal = ({
	children,
	targetId,
	className,
}: ConditionalPortalProps): JSX.Element => (targetId ? (
		<Portal {...{
			targetId,
			className,
		}}>
			{children}
		</Portal>
) : (
	children
));

export default function Tooltip({
	message,
	showing,
	mousePos = { x: 0, y: 0 },
	colorStyle = '',
	targetRef,
	position = TTPosition.above,
}: TooltipProps) {
	const tooltipRef = useRef<HTMLDivElement | null>(null);

	const [hasShownOnce, setHasShownOnce] = useState(false);
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);

	useEffect(() => {
		setWidth(tooltipRef.current?.offsetWidth ?? 0);
		setHeight(tooltipRef.current?.offsetHeight ?? 0);
	}, []);

	useLayoutEffect(() => {
		setWidth(tooltipRef.current?.offsetWidth ?? 0);
		setHeight(tooltipRef.current?.offsetHeight ?? 0);
	}, [message]);

	const getMousePosAbove = (animation: string) => ({
		animation,
		left: mousePos.x + (-width / 2),
		top: mousePos.y + (-height - 8),
	});

	const getVerticalPosition = (animation: string) => {
		const parentRect = targetRef.current?.getBoundingClientRect();
		if (!parentRect) {
			return getMousePosAbove(animation);
		}
		const parentCenterX = parentRect.left + (parentRect.width / 2) ?? 0;
		const parentCenterY = parentRect.top;

		let nLeft = parentCenterX - (width / 2);
		const nTop = position === TTPosition.below
			? parentCenterY + (parentRect.height + 16)
			: parentCenterY - (height + 8);

		// reposition tooltip if would be off the page
		if (nLeft < 0) {
			// nLeft = 0;
		} else if (nLeft + width > window.innerWidth) {
			nLeft = window.innerWidth - width;
		}

		return {
			animation,
			left: nLeft,
			top: nTop,
		};
	};

	const getHorizontalPosition = (animation: string) => {
		const parentRect = targetRef.current?.getBoundingClientRect();
		if (!parentRect) {
			return getMousePosAbove(animation);
		}

		const nLeft = position === TTPosition.left
			? parentRect.left - width - 12
			: parentRect.left + parentRect.width + 12;
		const nTop = parentRect.top + (parentRect.height / 2) - (height / 2);

		return {
			animation,
			left: nLeft,
			top: nTop,
		};
	};

	const getTrueMousePos = (animation: string) => {
		// detect if is near top or right of screen
		const percDownThePage = (mousePos.y / window.innerHeight);
		const percAcrossThePage = (mousePos.x / window.innerWidth);
		return {
			animation,
			left: mousePos.x + (percAcrossThePage < 0.8 ? 4 : (-width - 4)),
			top: mousePos.y + (percDownThePage < 0.2 ? 20 : -height),
		};
	};

	const currentPosition = useMemo((): TooltipPosition => {
		let animation = showing ? '150ms showTooltip forwards' : '150ms hideTooltip forwards';

		// because the animation starts at 1 opacity, it would flicker on load
		if (!hasShownOnce && !showing) {
			animation = '';
		} else if (showing) {
			setHasShownOnce(true);
		}

		if (position === TTPosition.mouse) {
			return getTrueMousePos(animation);
		}

		if (position === TTPosition.left || position === TTPosition.right) {
			return getHorizontalPosition(animation);
		}

		return getVerticalPosition(animation);
	}, [showing, mousePos, width, height, position]);

	const trianglePos = useMemo(() => {
		if (position === TTPosition.mouse) {
			return { display: 'none' };
		}
		if (position === TTPosition.left || position === TTPosition.right) {
			return {
				left: position === TTPosition.left ? width - 4 : -4,
				top: (height / 2) - 4,
			};
		}
		return {
			left: (width / 2) - 4,
			top: position === TTPosition.below ? -4 : (height - 4),
		};
	}, [currentPosition, position, height, width]);

	return (
		<ConditionalPortal
			targetId={'generic-tooltip'}
			className={`tooltip-portal${message ? ' has-message' : ''}`}
		>
			<div className={'tooltip-wrapper'} style={currentPosition}>
				<div className={`tooltip-message ${colorStyle}`} ref={tooltipRef}>
					{message}
					<div className="tooltip-triangle" style={trianglePos}/>
				</div>
			</div>
		</ConditionalPortal>
	);
}
